import React from "react"
import { useBoolean } from "@design-system/hooks"

import { styled, forwardRef, createStyles } from "@design-system/core"

import { RadioProps } from "./types"
import { useRadio } from "./use-radio"

export const Radio = forwardRef<RadioProps, "div">((props, ref) => {
  const { children } = props

  const {
    state: { isFocused, isChecked, isDisabled, isInvalid },
    getInputProps,
    htmlProps,
  } = useRadio(props)

  const inputProps = getInputProps({}, ref)

  return (
    <styled.label
      style={[styles.container, isDisabled && styles.containerDisabled]}
      {...htmlProps}
    >
      <styled.input {...inputProps} style={styles.input} />

      <styled.span
        style={[
          outerControlStyle.base,
          isChecked && outerControlStyle.active,
          isDisabled && outerControlStyle.disabled,
          isFocused && outerControlStyle.focus,
          isInvalid && outerControlStyle.invalid,
          isDisabled && isChecked && outerControlStyle.disabledAndChecked,
        ]}
      >
        <styled.span
          style={[
            innerControlStyle.base,
            isChecked && isDisabled && innerControlStyle.disabled,
            isChecked && !isDisabled && innerControlStyle.active,
          ]}
        />
      </styled.span>

      {children && (
        <styled.span
          style={[
            labelStyle.base,
            isChecked && labelStyle.isChecked,
            isDisabled && labelStyle.disabled,
          ]}
        >
          {children}
        </styled.span>
      )}
    </styled.label>
  )
})

const styles = createStyles((theme) => ({
  container: {
    display: "inline-flex",
    alignItems: "center",
    textAlignVertical: "top",
    cursor: "pointer",
  },
  containerDisabled: {
    cursor: "default",
  },
  input: {
    border: 0,
    clip: "rect(0px, 0px, 0px, 0px)",
    height: theme.sizes[1],
    width: theme.sizes[1],
    margin: -1,
    padding: theme.sizes[0],
    overflow: "hidden",
    whiteSpace: "nowrap" as "nowrap",
    position: "absolute" as "absolute",
  },
}))

const outerControlStyle = createStyles((theme) => ({
  base: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: theme.sizes[20],
    transition: "box-shadow 250ms",
    borderWidth: theme.borderWidths.small,
    borderStyle: "solid",
    borderColor: theme.colors.gray400,
    borderRadius: theme.borderRadii.full,
    color: theme.colors.white,
    height: theme.sizes[20],
  },
  active: {
    borderColor: theme.borderColors.selection,
  },
  disabled: {
    borderColor: theme.borderColors.disabled,
    backgroundColor: theme.colors.gray050,
  },
  disabledAndChecked: {
    borderColor: theme.borderColors.disabled,
    backgroundColor: theme.colors.white,
  },
  focus: {
    shadowColor: theme.colors.blue600,
    shadowRadius: theme.borderRadii.small,
  },
  invalid: {
    borderColor: theme.borderColors.error,
  },
}))

const innerControlStyle = createStyles((theme) => ({
  base: {
    width: theme.sizes[12],
    height: theme.sizes[12],
    borderRadius: theme.borderRadii.full,
  },
  active: {
    backgroundColor: theme.colors.blue400,
  },
  disabled: {
    backgroundColor: theme.colors.gray200,
  },
}))

const labelStyle = createStyles((theme) => ({
  base: {
    userSelect: "none",
    fontWeight: theme.fontWeights.regular,
    marginLeft: theme.spaces.spacing8,
    fontSize: theme.textFontSizes.medium,
  },
  isChecked: {
    fontWeight: theme.fontWeights.semibold,
  },
  disabled: {
    color: theme.colors.gray400,
    fontWeight: theme.fontWeights.regular,
  },
}))
